import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Dropdown, Menu, Spin, notification } from 'antd';
import Icon from '@ant-design/icons';
import { fetchApi } from 'core/utils/api';
import { VISITOR_REQUEST_STATUS } from 'core/utils/value';
import Status from 'components/Form/Status';
import { TriangleDownIcon, TriangleUpIcon, CancelIcon } from 'components/Elements/CustomSVGIcon';
import { VisitorRequestReview } from 'core/visitRequestReview/types';
import ActionModal, { ActionModalHandlers } from 'components/Modals/ActionModal';
import FormDetails from 'components/Form/FormDetails';
import ViewTable from 'pages/VisitorRequestView/components/ViewTable';
import 'pages/VisitorRequestView/VisitorRequestView.scss';
import 'pages/VisitorRequestView/components/ActionDropdown/actionDropdown.scss';
import Main from '../../components/layout/Main';
import './guestViewRequest.scss';

interface GuestViewResponse {
    visitRequest: VisitorRequestReview;
    statusData: { currentStatusStep: number };
    canCancel: boolean;
}

const STATUS_LABELS = ['Pending Approval', 'Rejected or Queried', 'Approved', 'Completed'];

/**
 * Public, token-gated guest view of a submitted request. Mirrors the internal
 * VisitorRequestView UI (Status, overview cards, ViewTable, the "Actions"
 * button) but the guest is unauthenticated and the only action is Cancel.
 * Loaded via the no-auth API using the revocable view token from the email.
 */
const GuestViewRequest: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<GuestViewResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [cancelOpen, setCancelOpen] = useState<boolean>(false);
    const [cancelled, setCancelled] = useState<boolean>(false);
    const actionModalRef = useRef<ActionModalHandlers>(null);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const response = await fetchApi({ method: 'GET', url: `guest/view/${token}` });
                if (active) {
                    setData(response.data);
                }
            } catch (e) {
                if (active) {
                    // Shown full-page below — no toast, to avoid doubling up.
                    setError('This link is invalid, has expired, or the request is no longer available.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => { active = false; };
    }, [token]);

    const handleCancel = async (_action?: string, message?: string) => {
        setCancelOpen(false);
        try {
            await fetchApi({
                method: 'POST',
                url: `guest/view/${token}/cancel`,
                body: { message: (message || '').trim() },
            });
            setCancelled(true);
            setData((prev) => (prev ? { ...prev, canCancel: false } : prev));
            actionModalRef.current?.clear();
            notification.success({
                message: 'Request cancelled',
                description: 'Your request has been cancelled. A confirmation email is on its way.',
            });
        } catch (e) {
            notification.error({
                message: 'Unable to cancel',
                description: 'We could not cancel this request. It may no longer be cancellable. Please try again.',
            });
        }
    };

    const mainProps = {
        className: 'login',
        title: <div>Visitor Request</div>,
        toolbar: [] as object[],
        menuPlacement: 'right' as const,
        menuWidth: 350,
        floatingHeader: false,
        floatingFooter: false,
        showFooter: false,
        footerContent: '',
    };

    if (loading) {
        return (
            <Main {...mainProps}>
                <div className="guest-view guest-view--centered">
                    <Spin size="large" />
                </div>
            </Main>
        );
    }

    if (error || !data) {
        return (
            <Main {...mainProps}>
                <div className="guest-view guest-view--centered">
                    <p className="guest-view__message">{error || 'Request not found.'}</p>
                </div>
            </Main>
        );
    }

    const { visitRequest, statusData, canCancel } = data;
    const canCancelNow = canCancel && !cancelled;
    const actionButtonIcon = isDropdownOpen ? TriangleUpIcon : TriangleDownIcon;

    const infoCard = [
        { id: 1, title: 'Requester', content: visitRequest.guestRequesterName || visitRequest.requester?.name || visitRequest.onBehalfEmail },
        {
            id: 2,
            title: 'Site',
            content: (
                <>
                    {visitRequest.site?.storeCode
                        ? `${visitRequest.site.name} (${visitRequest.site.storeCode})`
                        : visitRequest.site?.name}
                    {visitRequest.site?.staticSiteCode && (
                        <>
                            <br />
                            <span className="site-static-code">
                                Site Code: {visitRequest.site.staticSiteCode}
                            </span>
                        </>
                    )}
                </>
            ),
        },
        { id: 3, title: 'Date Submitted', content: visitRequest.dateSubmitted },
        { id: 4, title: 'Work Type', content: visitRequest.workTypeName },
        { id: 5, title: 'Number of Visitors', content: visitRequest.visitorsNumber ?? visitRequest.visitors?.length },
        { id: 6, title: 'Dates Requested', content: `${visitRequest.dates?.length ?? 0} Days` },
    ];

    // Same look as the internal ActionDropdown, but the guest's only action is
    // Cancel (disabled when the request is no longer cancellable).
    const renderActionsDropdown = () => (
        <Menu
            className="dropdown"
            style={{ boxShadow: '0px 0px 5px 3px rgba(43, 45, 66, 0.05)' }}
            onMouseLeave={() => setIsDropdownOpen(false)}>
            <Menu.Item
                key="cancel"
                className="action-dropdown-item-wrapper"
                disabled={!canCancelNow}
                onClick={() => { setIsDropdownOpen(false); setCancelOpen(true); }}>
                <div className="action-dropdown-item">
                    <span>
                        <CancelIcon s={!canCancelNow ? '#C0D1D8' : ''} />
                        Cancel
                    </span>
                </div>
            </Menu.Item>
        </Menu>
    );

    return (
        <Main {...mainProps} subHeaderTitle={`Visitor Request #${visitRequest.id}`}>
            <div className="main-visitor-container">
                <div className="visitor-request-view">
                    <Status currentStatus={Number(statusData?.currentStatusStep) || 1} labels={STATUS_LABELS} />
                    <div style={{ marginTop: 50 }}>
                        <h1>Overview</h1>
                        <div className="overview-container">
                            <div className="overview-card-container">
                                {infoCard.map((card) => (
                                    <div key={card.id} className="overview-card">
                                        <div className="overview-content">
                                            <span className="overview-text">{card.title}</span>
                                            <span className="overview-content-text">{card.content || 'N/A'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Dropdown
                                dropdownRender={renderActionsDropdown}
                                trigger={['click']}
                                align={{ offset: [-104, 10] }}
                                open={isDropdownOpen}>
                                <Button
                                    type="primary"
                                    style={{
                                        width: 204,
                                        marginTop: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingLeft: 40,
                                        paddingRight: 22,
                                    }}
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                    Actions
                                    <span className="vertical-line" />
                                    <Icon component={actionButtonIcon as unknown as React.ForwardRefExoticComponent<unknown>} />
                                </Button>
                            </Dropdown>
                        </div>
                        <div>
                            <ViewTable data={visitRequest} />
                        </div>
                    </div>
                </div>
                <div style={{ backgroundColor: '#EDF2F4', alignItems: 'center', display: 'grid', width: '100vw', marginTop: 50 }}>
                    <FormDetails
                        details={visitRequest}
                        isCostApproverByPass={visitRequest.costApproverByPass}
                        hasCostApprove={visitRequest.hasCostApprove} />
                </div>
            </div>

            <ActionModal
                ref={actionModalRef}
                visible={cancelOpen}
                action={VISITOR_REQUEST_STATUS.cancel}
                onConfirm={(action, message) => handleCancel(action, message)}
                onCancel={() => setCancelOpen(false)} />
        </Main>
    );
};

export default GuestViewRequest;
